import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { ImageThumbnail } from "../../components/upload/ImageUpload.jsx";

const STATUS_TONE = { draft: "default", published: "success" };

export default function ServicesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Services · Admin";
  }, []);

  const { list, remove } = useResource("services", { toastLabel: "service" });
  const { data, isLoading } = list({
    page,
    limit: 12,
    sort: "sortOrder:asc",
    ...(search && { search }),
    ...(status && { status }),
  });

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Content</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Services</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} service{data?.meta?.total === 1 ? "" : "s"} total
          </p>
        </div>
        <Link to="/services/new">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New service</Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search services by title…"
          filters={
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="min-w-[140px]"
            >
              <option value="">Any status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </Select>
          }
        />

        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          onRowClick={(row) => navigate(`/services/${row._id}`)}
          columns={[
            {
              key: "title",
              label: "Service",
              render: (r) => (
                <div className="flex items-center gap-3">
                  <ImageThumbnail src={r.heroImage} alt={r.title} />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text-primary">{r.title}</p>
                    <p className="truncate text-xs text-text-muted">
                      {r.shortDescription
                        ? r.shortDescription.slice(0, 80) + (r.shortDescription.length > 80 ? "…" : "")
                        : "—"}
                    </p>
                  </div>
                </div>
              ),
            },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <div className="flex items-center gap-1.5">
                  <Badge tone={STATUS_TONE[r.status] ?? "default"}>{r.status}</Badge>
                  {r.isFeatured && <Badge tone="accent">featured</Badge>}
                </div>
              ),
            },
            { key: "sortOrder", label: "Sort", render: (r) => r.sortOrder ?? 0 },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => navigate(`/services/${row._id}`)}
              onDelete={() => setConfirmDelete(row)}
            />
          )}
          empty={{
            title: "No services yet",
            description: "Create your first service to populate the public services page.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.title}"?`}
        description="The service will be removed from the public services page immediately."
        confirmLabel="Delete service"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}
